import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Loader2, 
  Download, 
  Wallet, 
  Crown, 
  Star, 
  Users,
  Smartphone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const tierMeta = {
  par: { name: 'Par Member', icon: Users, color: 'from-gray-700 to-gray-900', badge: 'bg-gray-200 text-gray-800' },
  birdie: { name: 'Birdie Member', icon: Star, color: 'from-gold to-amber-700', badge: 'bg-gold text-deep-green' },
  eagle: { name: 'Eagle Member', icon: Crown, color: 'from-deep-green to-emerald-900', badge: 'bg-deep-green text-gold' },
};

const MemberCardPage = () => {
  const { isAuthenticated } = useAuth();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) { setLoading(false); return; }
    const fetchCard = async () => {
      try {
        const token = localStorage.getItem('firebase_token');
        const res = await axios.get(`${API_URL}/member-card`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCard(res.data);
      } catch (err) {
        console.error('Failed to fetch member card:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [isAuthenticated]);

  const handleAddToGoogleWallet = () => {
    if (!card) return;
    const walletUrl = `https://pay.google.com/gp/v/save/eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9`;
    toast.info('Google Wallet integration will be available soon. Use the download option for now.');
  };

  const handleAddToAppleWallet = () => {
    if (!card) return;
    toast.info('Apple Wallet integration will be available soon. Use the download option for now.');
  };

  const handleDownloadCard = () => {
    const canvas = document.getElementById('member-card-canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'fairway-alliance-member-card.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Card downloaded!');
  };

  const drawCard = () => {
    if (!card) return;
    const canvas = document.getElementById('member-card-canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = 800, h = 500;
    canvas.width = w;
    canvas.height = h;

    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, w, h);
    grad.addColorStop(0, '#0A2F25');
    grad.addColorStop(1, '#0B3B2E');
    ctx.fillStyle = grad;
    ctx.roundRect(0, 0, w, h, 20);
    ctx.fill();

    // Gold accent line
    ctx.fillStyle = '#C8A24A';
    ctx.fillRect(40, 80, 720, 2);

    // Logo text
    ctx.fillStyle = '#C8A24A';
    ctx.font = 'bold 28px Georgia, serif';
    ctx.fillText('FAIRWAY ALLIANCE', 40, 60);

    // Tier badge
    ctx.fillStyle = '#C8A24A';
    ctx.font = 'bold 14px Arial, sans-serif';
    const tierName = (tierMeta[card.tier]?.name || 'Gallery Member').toUpperCase();
    ctx.fillText(tierName, 40, 120);

    // Member name
    ctx.fillStyle = '#F3E7C6';
    ctx.font = 'bold 36px Georgia, serif';
    ctx.fillText(card.display_name, 40, 180);

    // Member details
    ctx.fillStyle = '#F3E7C699';
    ctx.font = '16px Arial, sans-serif';
    ctx.fillText(`Member ID: ${card.member_id}`, 40, 230);
    if (card.home_city && card.home_state) {
      ctx.fillText(`Home Course: ${card.home_city}, ${card.home_state}`, 40, 260);
    }
    if (card.handicap) {
      ctx.fillText(`Handicap: ${card.handicap}`, 40, 290);
    }
    ctx.fillText(`Member Since: ${card.member_since}`, 40, 320);

    // QR code
    const qrImg = new Image();
    qrImg.crossOrigin = 'anonymous';
    qrImg.onload = () => {
      ctx.drawImage(qrImg, 580, 160, 180, 180);
      // "Scan for verification" text
      ctx.fillStyle = '#F3E7C680';
      ctx.font = '11px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Scan to verify', 670, 360);
      ctx.textAlign = 'left';
    };
    qrImg.src = card.qr_code;

    // Bottom bar
    ctx.fillStyle = '#C8A24A20';
    ctx.fillRect(0, 430, w, 70);
    ctx.fillStyle = '#C8A24A';
    ctx.font = '12px Arial, sans-serif';
    ctx.fillText('Show this card at partner courses & shops for member discounts', 40, 460);
    ctx.fillText('fairwayalliance.com', 40, 480);
  };

  useEffect(() => {
    if (card) {
      setTimeout(drawCard, 200);
    }
  }, [card]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bone pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-fairway" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-bone pt-24 flex items-center justify-center">
        <div className="text-center">
          <Wallet className="w-12 h-12 text-gold mx-auto mb-4" />
          <h2 className="font-display font-bold text-2xl text-deep-green mb-2">Sign In Required</h2>
          <p className="text-muted-foreground mb-4">Log in to access your digital member card</p>
        </div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-screen bg-bone pt-24 flex items-center justify-center">
        <p className="text-muted-foreground">Unable to load member card</p>
      </div>
    );
  }

  const meta = tierMeta[card.tier] || tierMeta.gallery;
  const TierIcon = meta.icon;

  return (
    <div className="min-h-screen bg-bone pt-24 pb-20">
      <div className="bg-deep-green py-12 mb-8">
        <div className="container-padding">
          <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-2">Your Membership</p>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-3" data-testid="member-card-title">
            Digital Member Card
          </h1>
          <p className="text-cream/70 font-body max-w-2xl">
            Show this card at partner courses and shops for exclusive member discounts.
          </p>
        </div>
      </div>

      <div className="container-padding max-w-3xl mx-auto">
        {/* Visual Card Preview */}
        <div className={`relative bg-gradient-to-br ${meta.color} rounded-2xl p-8 md:p-10 shadow-2xl mb-8 overflow-hidden`} data-testid="member-card-visual">
          {/* Decorative circles */}
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/5" />
          <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-white/5" />

          <div className="relative z-10 flex flex-col md:flex-row gap-8">
            <div className="flex-1">
              {/* Logo */}
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="/logo.png" 
                  alt="Fairway Alliance"
                  className="h-10 w-auto"
                />
                <div className="h-6 w-px bg-white/30" />
                <Badge className={meta.badge}>
                  <TierIcon className="w-3 h-3 mr-1" />
                  {meta.name}
                </Badge>
              </div>

              {/* Name */}
              <h2 className="font-display font-bold text-3xl text-white mb-1">{card.display_name}</h2>
              <p className="text-white/60 text-sm mb-6">ID: {card.member_id}</p>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                {card.home_city && card.home_state && (
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Home Course</p>
                    <p className="text-white font-semibold">{card.home_city}, {card.home_state}</p>
                  </div>
                )}
                {card.handicap && (
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Handicap</p>
                    <p className="text-white font-semibold">{card.handicap}</p>
                  </div>
                )}
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Member Since</p>
                  <p className="text-white font-semibold">{card.member_since}</p>
                </div>
              </div>
            </div>

            {/* QR Code */}
            <div className="flex-shrink-0 flex flex-col items-center">
              <div className="bg-cream rounded-lg p-3">
                <img src={card.qr_code} alt="QR Code" className="w-36 h-36" data-testid="member-card-qr" />
              </div>
              <p className="text-white/40 text-xs mt-2">Scan to verify</p>
            </div>
          </div>

          {/* Bottom */}
          <div className="relative z-10 mt-6 pt-4 border-t border-white/10 flex justify-between items-center">
            <p className="text-white/40 text-xs">Show at partner courses & shops for discounts</p>
            <p className="text-white/40 text-xs">fairwayalliance.com</p>
          </div>
        </div>

        {/* Hidden canvas for download */}
        <canvas id="member-card-canvas" className="hidden" />

        {/* Action Buttons */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Button 
            onClick={handleDownloadCard} 
            className="py-6 bg-deep-green hover:bg-deep-green/90 text-white"
            data-testid="download-card-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            Save as Image
          </Button>
          <Button 
            onClick={handleAddToGoogleWallet} 
            variant="outline" 
            className="py-6 border-2 border-deep-green text-deep-green hover:bg-deep-green hover:text-white"
            data-testid="google-wallet-btn"
          >
            <Smartphone className="w-5 h-5 mr-2" />
            Add to Google Wallet
          </Button>
          <Button 
            onClick={handleAddToAppleWallet} 
            variant="outline" 
            className="py-6 border-2 border-deep-green text-deep-green hover:bg-deep-green hover:text-white"
            data-testid="apple-wallet-btn"
          >
            <Wallet className="w-5 h-5 mr-2" />
            Add to Apple Wallet
          </Button>
        </div>

        {/* Info */}
        <div className="bg-white rounded-sm shadow-sm p-6 text-center">
          <p className="text-muted-foreground text-sm font-body">
            Present your digital member card at any Fairway Alliance partner location to receive your membership benefits and discounts. 
            Your unique QR code verifies your membership status instantly.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MemberCardPage;
